import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen } from 'lucide-react';
import { SeoMeta } from '../components/seo/SeoMeta';

const BlogPost19: React.FC = () => {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [surname, setSurname] = useState('');

  const handleGenerateName = (e: React.FormEvent) => {
    e.preventDefault();
    if (firstName.trim() && surname.trim()) {
      navigate('/chinese-name-generator', { 
        state: { 
          firstName: firstName.trim(),
          surname: surname.trim(),
          autoGenerate: true 
        } 
      });
    }
  };

  return (
    <>
      <SeoMeta
        title="What Is Your Name in Chinese? | Interactive Guide"
        description="Want to know what your name is in Chinese? Use our interactive guide to translate your English name instantly. Learn how phonetic matching works (sounds similar), meaning translation (similar meaning), or discover a completely new Chinese name that fits your personality. Includes examples, pronunciation guides, and our free AI name generator. Get your authentic Chinese name in seconds."
        keywords="what is your name in chinese, what is my name in chinese, translate your name to chinese, your name in chinese, find my name in chinese"
        pathname="/blog/what-is-your-name-in-chinese"
      />

      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <nav className="bg-white shadow-sm border-b">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <Link to="/blog" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium">
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>
          </div>
        </nav>

        <header className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium mb-6">
              <BookOpen className="w-4 h-4" />
              Interactive Guide
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              What Is Your Name in Chinese? Interactive Guide
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Discover your Chinese name with our interactive guide. Learn how to translate your name using phonetic matching and meaning-based selection. Find out <strong>what is your name in Chinese</strong> and understand the translation process.
            </p>
          </div>
        </header>

        <article className="max-w-4xl mx-auto px-4 pb-16">
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Understanding Name Translation</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              When asking <strong>"what is your name in Chinese"</strong>, there are several approaches to finding the answer. The translation process involves understanding both phonetic matching and cultural considerations to create an authentic Chinese name.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Our interactive guide helps you discover <strong>what is your name in Chinese</strong> by providing multiple translation options, each with its own cultural significance and meaning.
            </p>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Find Your Chinese Name</h2>
            <p className="text-gray-700 mb-6">
              Use our AI-powered generator to discover <strong>what is your name in Chinese</strong>. Get multiple translation options with meanings and cultural explanations.
            </p>
            <form onSubmit={handleGenerateName} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First Name"
                  className="px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
                <input
                  type="text"
                  value={surname}
                  onChange={(e) => setSurname(e.target.value)}
                  placeholder="Last Name"
                  className="px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <button
                type="submit"
                className="w-full bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors font-medium"
              >
                Get My Chinese Name →
              </button>
            </form>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Translation Methods</h2>
            <div className="space-y-4">
              <div className="bg-blue-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Phonetic Translation</h3>
                <p className="text-gray-700">
                  Matches the sounds of your English name with similar-sounding Chinese characters. This method helps answer <strong>"what is your name in Chinese"</strong> by preserving the original pronunciation.
                </p>
              </div>
              <div className="bg-blue-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Meaning-Based Translation</h3>
                <p className="text-gray-700">
                  Selects Chinese characters based on the meaning of your name. This approach creates a name that reflects your name's essence when answering <strong>"what is your name in Chinese"</strong>.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Related Resources</h2>
            <ul className="space-y-3">
              <li>
                <Link to="/blog/my-name-in-chinese" className="text-blue-600 hover:underline font-medium">
                  My Name in Chinese Guide →
                </Link>
              </li>
              <li>
                <Link to="/" className="text-blue-600 hover:underline font-medium">
                  Chinese Name Generator →
                </Link>
              </li>
            </ul>
          </div>
        </article>
      </div>
    </>
  );
};

export default BlogPost19;

